import { DALI_ADDRESS, DALI_MASTER, DALI_MODE, DALI_PARAM } from '../../catalog/dali';
import type { DaliMode } from '../../catalog/dali';
import { RCU_CMDTYPE, RCU_TOP_TYPE } from '../../catalog/protocol';
import type { ConfigIssue } from '../../catalog/issue';
import type { Result } from '../../result';
import type { RcuDocument } from '../../wire';
import { fieldRange, validated } from './validate';

/**
 * DALI. The firmware validates the master and the mode but not the address or
 * the parameter, so the checks here are the app's own: an address outside the
 * protocol's ranges fails on the bus rather than at the parser, and finding that
 * out from a NAK is slower than being told before sending.
 */

export interface DaliAddressInput {
  readonly master: number;
  readonly address: number;
}

export interface OperateDaliInput extends DaliAddressInput {
  readonly mode: DaliMode;
  /** Dim level or colour temperature; a query always sends `DALI_PARAM.QUERY`. */
  readonly param: number;
}

const masterCheck = (master: number): ConfigIssue | null =>
  fieldRange('Master', master, DALI_MASTER.MIN, DALI_MASTER.MAX);

/** Any address the protocol defines: individual, group or broadcast. */
const addressCheck = (address: number): ConfigIssue | null =>
  fieldRange('Address', address, DALI_ADDRESS.INDIVIDUAL_MIN, DALI_ADDRESS.BROADCAST);

export const operateDali = (input: OperateDaliInput): Result<RcuDocument> =>
  validated(
    {
      type: RCU_TOP_TYPE.COMMAND,
      data: {
        cmdtype: RCU_CMDTYPE.OPERATE_DALI,
        master: input.master,
        daliaddr: input.address,
        mode: input.mode,
        /** A query is mode 0 with param 0; the firmware has no "read level" mode. */
        param: input.mode === DALI_MODE.QUERY ? DALI_PARAM.QUERY : input.param,
      },
    },
    [
      masterCheck(input.master),
      addressCheck(input.address),
      fieldRange('Parameter', input.param, DALI_PARAM.MIN, DALI_PARAM.MAX),
    ],
  );

/** No submit ack: the bus's answer arrives as a `daliack`. */
export const readDali = (input: DaliAddressInput): Result<RcuDocument> =>
  validated(
    {
      type: RCU_TOP_TYPE.COMMAND,
      data: {
        cmdtype: RCU_CMDTYPE.READ_DALI,
        master: input.master,
        daliaddr: input.address,
      },
    },
    [masterCheck(input.master), addressCheck(input.address)],
  );
